import React, { useEffect, useState } from 'react';
import { Flex, Spin, Typography, message, Modal } from 'antd';
import axios from '../../config/axiosInstance';
import ParticipantsFilters from './ParticipantsFilters';
import ParticipantsTable from './ParticipantsTable';
import ModalParticipantQR from './ModalParticipantQR';
import ModalParticipantDetails from './ModalParticipantDetails';
import useParticipantData from '../../hooks/participants/useParticipantData';
import { useTheme } from '../../context/ThemeContext';
import FullScreenCamera from "./ParticipantDetailsTabs/FullScreenCamera";

const { Title, Text } = Typography;

const ParticipantsList = () => {
    const { darkTheme } = useTheme();
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [filters, setFilters] = useState({
        eventPK: undefined,
        participantStatePKs: [],
        participantTypes: [],
        hasRegistration: false,
        hasBadge_Enabled: false,
        firstName: '',
        lastName: '',
    });
    const [selectedHost, setSelectedHost] = useState('');
    const [qrVisible, setQrVisible] = useState(false);
    const [qrValue, setQrValue] = useState('');
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [isCameraVisible, setIsCameraVisible] = useState(false);

    const { participantTypes, participantStates, programmePoints } = useParticipantData(filters.eventPK, selectedHost);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axios.get('/events');
                setEvents(response.data);
            } catch (error) {
                console.error('Error fetching events:', error);
                message.error('Error loading events');
            }
        };
        fetchEvents();
    }, []);

    const handleEventChange = (eventId) => {
        setFilters({
            eventPK: eventId,
            participantStatePKs: [],
            participantTypes: [],
            programmePointIds: [],
            hasRegistration: false,
            hasBadgeEnabled: false,
            firstName: '',
            lastName: '',
        });
        const selectedEvent = events.find(event => event.eventId === eventId);
        if (selectedEvent && selectedEvent.host_url) {
            setSelectedHost(selectedEvent.host_url);
        } else {
            setSelectedHost('');
            message.warning('Selected event does not have a host URL');
        }
    };

    const handleSearch = async () => {
        if (!selectedHost) {
            message.warning('Please select an event with a valid host URL.');
            return;
        }

        setLoading(true);
        try {
            const tokenResponse = await axios.get(`/regasus/token?host=${selectedHost}`);
            const token = tokenResponse.data.access_token;

            if (!token) {
                message.error('Token could not be retrieved');
                return;
            }

            // Construction of the bodyParams
            const bodyParams = {
                eventPK: filters.eventPK,
                hasRegistration: filters.hasRegistration ? '1' : undefined,
                hasBadge_Enabled: filters.hasBadge_Enabled ? '1' : undefined,
                firstName: filters.firstName,
                lastName: filters.lastName,
                participantType: filters.participantTypes.length > 0 ? filters.participantTypes : undefined,
                participantStatePK: filters.participantStatePKs.length > 0 ? filters.participantStatePKs : undefined,
                hasLead: filters.programmePointIds.length > 0 ? filters.programmePointIds : undefined,
            };

            const searchResponse = await axios.post(
                `/regasus/participants/search?host=${selectedHost}&token=${encodeURIComponent(token)}`,
                bodyParams
            );

            const data = searchResponse.data?.participantInfos?.participantInfo || [];

            const formatted = data.map((p, index) => ({
                key: p.id || index,
                vigenereCode: p.vigenereCode || '',
                firstName: p.firstName || '',
                lastName: p.lastName || '',
                city: p.city || '',
                organisation: p.organisation || '',
                zip: p.zip || '',
                email: p.email || '',
                number: p.number || '',
                participantType: p.participantType?.[0]?.value || '',
                participantState: p.participantState?.[0]?.value || '',
            }));

            setParticipants(formatted);
        } catch (error) {
            console.error('Error fetching participants:', error);
            message.error('Error fetching participants');
        } finally {
            setLoading(false);
        }
    };

    //Actions
    const showDetailsModal = (participant) => {
        setSelectedParticipant(participant);
        setDetailsModalOpen(true);
    };

    const showQRCode = (participant) => {
        const value = `${participant.vigenereCode};${participant.firstName};${participant.lastName};`;
        setQrValue(value);
        setSelectedParticipant(participant);
        setQrVisible(true);
    };

    const handlePrintBadge = async (participant, force = false, predefinedWindow = null) => {
        if (!selectedHost || !filters.eventPK) {
            message.warning('Please select an event first.');
            return;
        }

        try {
            const tokenResponse = await axios.get(`/regasus/token?host=${selectedHost}`);
            const token = tokenResponse.data.access_token;

            if (!token) {
                message.error('Token could not be retrieved');
                return;
            }

            let hasActiveBadge = false;

            if (!force) {
                const badgesResponse = await axios.post('/regasus/get-badges-of-participant', {
                    token,
                    host: selectedHost,
                    participantId: participant.key
                });

                const badges = Array.isArray(badgesResponse.data)
                    ? badgesResponse.data
                    : badgesResponse.data?.badges?.badge || [];

                hasActiveBadge = badges.some(b => !b.disabled);

                if (hasActiveBadge) {
                    // Pre-open the window here and pass it along
                    const win = window.open('', '_blank'); // This opens right when the user clicks

                    Modal.confirm({
                        title: 'Active Badge Detected',
                        content: 'This participant already has an active badge. Do you want to create a new one?',
                        okText: 'Yes',
                        cancelText: 'No',
                        onOk: () => handlePrintBadge(participant, true, win), // pass the window along
                    });

                    return;
                }
            }

            const response = await axios.post('/regasus/create-new-badge', {
                token,
                host: selectedHost,
                participantId: participant.key
            }, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const pdfUrl = window.URL.createObjectURL(blob);

            const win = predefinedWindow || window.open(pdfUrl, '_blank'); // reuse it if it is already open
            if (predefinedWindow) {
                predefinedWindow.location.href = pdfUrl;
            }

            if (!win) {
                message.warning('Popup blocked. Please allow popups for this site.');
            }
        } catch (error) {
            console.error('Error creating badge:', error);
            message.error('Could not create badge');
        }
    };

    const showCamera = (participant) => {
        setSelectedParticipant(participant);
        setIsCameraVisible(true);
    };

    const handleCloseCamera = () => {
        setIsCameraVisible(false);
    };

    return (
        <div style={{ padding: '24px' }}>
            <Title level={3}>Participants List</Title>

            <div style={{ marginBottom: 24 }}>
                <ParticipantsFilters
                    events={events}
                    participantTypes={participantTypes}
                    participantStates={participantStates}
                    programmePoints={programmePoints}
                    filters={filters}
                    setFilters={setFilters}
                    onEventChange={handleEventChange}
                    onSearchParticipants={handleSearch}
                />

                {!loading && (
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Text type="secondary">Participants found: <strong>{participants.length}</strong></Text>
                    </div>
                )}
            </div>

            {loading ? (
                <Flex
                    vertical
                    justify="center"
                    align="center"
                    style={{ minHeight: 300 }}
                >
                    <Spin size="large" />
                    <span style={{ marginTop: 16, fontSize: 16, color: '#1677ff' }}>
                        Searching participants...
                      </span>
                </Flex>
            ) : (
                <ParticipantsTable
                    data={participants}
                    onShowDetails={showDetailsModal}
                    onQRCodeClick={showQRCode}
                    onPrintBadgeClick={handlePrintBadge}
                    showCamera={showCamera}
                />
            )}

            <ModalParticipantDetails
                open={detailsModalOpen}
                participant={selectedParticipant}
                host={selectedHost}
                onClose={() => setDetailsModalOpen(false)}
                programmePoints={programmePoints}
                participantTypes={participantTypes}
                participantStates={participantStates}
            />

            <ModalParticipantQR
                open={qrVisible}
                value={qrValue}
                participant={selectedParticipant}
                host={selectedHost}
                onClose={() => setQrVisible(false)}
            />

            {/* Camera Modal */}
            {isCameraVisible && selectedParticipant && selectedParticipant.key && (
                <FullScreenCamera
                    visible={isCameraVisible}
                    onCancel={handleCloseCamera}
                    host={selectedHost}
                    participant={selectedParticipant}
                />
            )}
        </div>
    );
};

export default ParticipantsList;
